import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import { FormContext } from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { createTaskFormSchema } from 'modules/workflows/components/Sidepanel/schemas/taskFormSchema'

import { createEmployeeSegmentSchema } from './employeeSegmentSchema'

export const createCompleteFormHandlerSchema = (formContext: FormContext) => {
  const employeeSegmentSchema = createEmployeeSegmentSchema()

  return createTaskFormSchema(formContext).extend({
    nodeType: z.literal('task'),
    nodeData: z.object({
      handlerType: z.enum(['complete_form', 'vendor_onboarding']),
      formFields: z
        .array(
          z.object({
            elementId: z.string(),
            invariantId: z.string().optional(),
            label: z.string(),
            mandatory: z.boolean(),
            type: z.string(),
          })
        )
        .optional(),
      payload: z
        .object({
          form_id: z.number({
            message: i18n.t('procurement.workflows.complete_form_handler.validation.form_required'),
          }),
          employee_ids: z.array(z.string()).optional(),
          employee_segments: z.array(employeeSegmentSchema).optional(),
          fallback_employee_ids: z.array(z.string()).optional(),
          fallback_employee_segments: z.array(employeeSegmentSchema).optional(),
          due_on_value: z
            .number({
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            })
            .min(1, {
              message: i18n.t(
                'procurement.workflows.basic_task_handler.validation.due_date_min_value'
              ),
            }),
          due_on_unit: z.enum(['hours', 'days', 'weeks', 'months']),
          completion_mode: z.enum(['internal', 'external']).optional(),
        })
        .refine(
          (data) => {
            return data.form_id > 0
          },
          {
            message: i18n.t('procurement.workflows.complete_form_handler.validation.form_required'),
            path: ['form_id'],
          }
        )
        .refine(
          (data) => {
            const hasAssignees = !!data.employee_ids?.length
            const hasSegments = !!data.employee_segments?.length
            return hasAssignees || hasSegments
          },
          {
            message: i18n.t(
              'procurement.workflows.basic_task_handler.validation.assignee_required'
            ),
            path: ['employee_ids'],
          }
        ),
    }),
  })
}

export type CompleteFormHandlerSchema = z.infer<ReturnType<typeof createCompleteFormHandlerSchema>>
